import { PrimaryButton, GhostButton, IconButton } from '@ardoq/button';
import { colors, space } from '@ardoq/design-tokens';
import { ArrowForwardIcon, IconName } from '@ardoq/icons';
import { NewBadge, ReadonlyBadge, StatusType } from '@ardoq/status-ui';
import { Header4, Text } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { CarouselStatus } from './CarouselStatus';
import type { EphemeralExternalLink } from '../types';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { I18nLocale, t } from '@ardoq/i18n';

export type CarouselSize = 'S' | 'M' | 'L';

type CSSDimension = `${number}px`;

const carouselSizeToWidth: Record<CarouselSize, CSSDimension> = {
  S: '280px',
  M: '600px',
  L: '960px',
};

const CloseWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  color: ${colors.icon};
  z-index: 50;
`;

const ContentContainer = styled(Stack).attrs({
  gap: 'xsmall',
  align: 'flex-start',
})<{ $size: CarouselSize }>`
  width: ${({ $size }) => carouselSizeToWidth[$size]};
  padding: ${space.medium};
`;

const ContentWrapper = styled(Box).attrs({
  width: 'full',
  borderRadius: 'r8',
  overflow: 'hidden',
})``;

const CarouselContainer = styled(Box).attrs<{ $withShadow: boolean }>(
  ({ $withShadow }) => ({
    position: 'relative',
    backgroundColor: 'surfaceDefault',
    overflow: 'hidden',
    borderRadius: 'r8',
    boxShadow: $withShadow ? 'shadowM' : undefined,
  })
)<{ $size: CarouselSize; $withShadow: boolean }>`
  width: ${({ $size }) => carouselSizeToWidth[$size]};
`;

const CarouselViewport = styled.div<{ $height?: number }>`
  overflow: hidden;
  transition: height 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  ${({ $height }) =>
    $height !== undefined &&
    css`
      height: ${$height}px;
    `}
`;

const CarouselList = styled.ul<{ $currentCardIndex: number }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  list-style-type: none;
  padding: 0;
  margin: 0;
  ${({ $currentCardIndex }) => css`
    transform: translateX(${$currentCardIndex * -100}%);
  `}
`;

const FooterContainer = styled(FlexBox).attrs({
  align: 'center',
  justify: 'space-between',
  gap: 'medium',
  padding: 'medium',
})<{
  $hasCarouselStatus: boolean;
}>`
  ${({ $hasCarouselStatus }) => css`
    justify-content: ${$hasCarouselStatus ? 'space-between' : 'flex-end'};
  `}
`;

const ButtonsContainer = styled(FlexBox).attrs({
  align: 'center',
  gap: 'small',
})``;

export type CarouselCard = {
  content: React.ReactNode;
  header: string;
  bodyText: React.ReactNode;
  externalLink?: EphemeralExternalLink;
  isNew?: boolean;
  isBeta?: boolean;
};

export type CarouselConfigurableProps = {
  cards: CarouselCard[];
  size?: CarouselSize;
  withShadow?: boolean;
  showSkipButton?: boolean;
  skipButtonLabel?: string;
  /** nextButtonLabel/backButtonLabel are only rendered once the carousel has more than one card. */
  nextButtonLabel?: string;
  backButtonLabel?: string;
  endButtonLabel?: string;
  closeButtonLabel?: string;
  showCloseButton?: boolean;
};

export type CarouselProps = CarouselConfigurableProps & {
  onClose: VoidFunction;
  onPrimaryActionClick: VoidFunction;
  onSlideNavigate?: (fromSlideIndex: number, toSlideIndex: number) => void;
  onExternalLinkClick?: (slideIndex: number) => void;
  i18nLocale: I18nLocale;
};

export const Carousel = ({
  cards,
  onClose,
  onPrimaryActionClick,
  i18nLocale,
  size = 'L',
  withShadow = false,
  nextButtonLabel = t(
    i18nLocale,
    'ephemeralNotification:primaryButtonTitle',
    'Next'
  ),
  backButtonLabel = t(
    i18nLocale,
    'ephemeralNotification:secondaryButtonTitle',
    'Back'
  ),
  endButtonLabel = t(
    i18nLocale,
    'ephemeralNotification:endButtonTitle',
    'All caught up'
  ),
  closeButtonLabel = t(
    i18nLocale,
    'ephemeralNotification:closeButtonLabel',
    'Close'
  ),
  showCloseButton = true,
  showSkipButton,
  skipButtonLabel = t(
    i18nLocale,
    'ephemeralNotification:skipButtonTitle',
    'Skip'
  ),
  onSlideNavigate,
  onExternalLinkClick,
}: CarouselProps) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const carouselRef = useRef<HTMLUListElement>(null);
  const [activeCardHeight, setActiveCardHeight] = useState<number | undefined>(
    undefined
  );
  const [hasLeftFirstSlide, setHasLeftFirstSlide] = useState(false);

  const goToSlide = (index: number) => {
    onSlideNavigate?.(currentCardIndex, index);
    if (index !== 0) {
      setHasLeftFirstSlide(true);
    }
    setCurrentCardIndex(index);
  };

  useLayoutEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    const activeCard = carousel.children[currentCardIndex] as
      | HTMLElement
      | undefined;
    if (!activeCard) return;

    const updateHeight = () => setActiveCardHeight(activeCard.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(activeCard);
    return () => observer.disconnect();
  }, [currentCardIndex]);

  const hasCarouselStatus = cards.length > 1;

  return (
    <CarouselContainer $size={size} $withShadow={withShadow}>
      {showCloseButton && (
        <CloseWrapper>
          <IconButton
            onClick={onClose}
            label={closeButtonLabel}
            iconName={IconName.CLOSE}
          />
        </CloseWrapper>
      )}
      <CarouselViewport $height={activeCardHeight}>
        <CarouselList ref={carouselRef} $currentCardIndex={currentCardIndex}>
          {cards.map(
            (
              {
                isNew = false,
                isBeta = false,
                content,
                header,
                bodyText,
                externalLink,
              },
              cardIndex
            ) => {
              const isHidden = cardIndex !== currentCardIndex;
              return (
                <li key={`CarouselFeature${cardIndex}`} inert={isHidden}>
                  <ContentContainer $size={size}>
                    <ContentWrapper>{content}</ContentWrapper>
                  </ContentContainer>
                  <FlexBox paddingX="medium">
                    <Stack gap="medium" align="flex-start">
                      {(isNew || isBeta) && (
                        <FlexBox gap="xsmall" align="center">
                          {isNew && <NewBadge i18nLocale={i18nLocale} />}
                          {isBeta && (
                            <ReadonlyBadge
                              label={t(
                                i18nLocale,
                                'ephemeralNotification:betaBadgeLabel',
                                'Beta'
                              )}
                              statusType={StatusType.BETA}
                              iconName={IconName.BETA}
                            />
                          )}
                        </FlexBox>
                      )}
                      <Header4>{header}</Header4>
                      <Text variant="text2" color="textModerate">
                        {bodyText}
                      </Text>
                      {externalLink && (
                        <Link
                          typography="text2"
                          href={externalLink.url}
                          target="_blank"
                          rel="noreferrer"
                          keepDefaultNavigation
                          onAuxClick={e => {
                            if (e.button === 1) {
                              onExternalLinkClick?.(currentCardIndex);
                            }
                          }}
                          onClick={() =>
                            onExternalLinkClick?.(currentCardIndex)
                          }
                        >
                          {externalLink.text}
                        </Link>
                      )}
                    </Stack>
                  </FlexBox>
                </li>
              );
            }
          )}
        </CarouselList>
      </CarouselViewport>
      <FooterContainer $hasCarouselStatus={hasCarouselStatus}>
        {hasCarouselStatus && (
          <CarouselStatus
            count={cards.length}
            setStep={toSlideIndex => {
              if (toSlideIndex === currentCardIndex) {
                return;
              }
              goToSlide(toSlideIndex);
            }}
            currentIndex={currentCardIndex}
          />
        )}
        <ButtonsContainer>
          {currentCardIndex === 0 && showSkipButton && !hasLeftFirstSlide && (
            <GhostButton onClick={onClose}>{skipButtonLabel}</GhostButton>
          )}
          {currentCardIndex - 1 >= 0 && (
            <GhostButton
              onClick={() => {
                goToSlide(currentCardIndex - 1);
              }}
            >
              {backButtonLabel}
            </GhostButton>
          )}
          {currentCardIndex + 1 < cards.length && (
            <PrimaryButton
              onClick={() => {
                goToSlide(currentCardIndex + 1);
              }}
            >
              {nextButtonLabel}
              <ArrowForwardIcon />
            </PrimaryButton>
          )}
          {currentCardIndex === cards.length - 1 && (
            <PrimaryButton onClick={onPrimaryActionClick}>
              {endButtonLabel}
            </PrimaryButton>
          )}
        </ButtonsContainer>
      </FooterContainer>
    </CarouselContainer>
  );
};
